import React from "react";
import { motion } from "framer-motion";

/**
 * Cinematic ink-reveal title.
 * Renders the title twice, layered:
 *   - Layer A: SVG-text outline drawn slowly (pen stroke writing on paper)
 *   - Layer B: Filled HTML text fading in beneath, line by line
 * The dual-layer effect feels like ink soaking into paper after the pen passes.
 *
 * Props:
 *   text: string — title content
 *   className: outer wrapper classes
 *   color: ink color (default tobacco)
 */
export default function InkRevealTitle({ text, className = "", color = "#4B2E1F" }) {
  // Split into ~equal lines for a multi-line effect; respects spaces only.
  const lines = splitIntoLines(text, 28);

  return (
    <div className={`relative ${className}`} aria-label={text}>
      <h1 className="font-display leading-[1.02] tracking-tight text-[10vw] md:text-[6.4vw] lg:text-[5.4vw]" style={{ color }}>
        {lines.map((line, i) => (
          <span key={i} className="relative block overflow-hidden">
            {/* filled text fading in */}
            <motion.span
              className="block"
              initial={{ opacity: 0, y: "0.4em" }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.6,
                delay: 1.0 + i * 0.6,
                ease: [0.65, 0, 0.35, 1],
              }}
            >
              {line}
            </motion.span>

            {/* ink-stroke overlay drawn first, then fades out as the fill settles */}
            <motion.svg
              aria-hidden
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 1000 120"
              preserveAspectRatio="none"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{
                duration: 1.6,
                delay: 2.2 + i * 0.6,
                ease: [0.65, 0, 0.35, 1],
              }}
            >
              <defs>
                <clipPath id={`clip-${i}`}>
                  <motion.rect
                    x="0"
                    y="0"
                    width="1000"
                    height="120"
                    initial={{ width: 0 }}
                    animate={{ width: 1000 }}
                    transition={{
                      duration: 2.0,
                      delay: 0.2 + i * 0.6,
                      ease: [0.65, 0, 0.35, 1],
                    }}
                  />
                </clipPath>
              </defs>
              <text
                x="0"
                y="92"
                fontFamily="Cormorant Garamond, serif"
                fontStyle="italic"
                fontSize="110"
                fontWeight="400"
                fill="none"
                stroke={color}
                strokeWidth="1"
                strokeOpacity="0.55"
                clipPath={`url(#clip-${i})`}
                style={{ letterSpacing: "-0.01em" }}
              >
                {line}
              </text>
            </motion.svg>
          </span>
        ))}
      </h1>
    </div>
  );
}

function splitIntoLines(text, maxChars) {
  if (!text) return [""];
  const words = text.split(" ");
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars && cur) {
      lines.push(cur.trim());
      cur = w;
    } else {
      cur = cur ? cur + " " + w : w;
    }
  }
  if (cur) lines.push(cur.trim());
  return lines;
}
